/**
 * Admin Service
 * API client for privileged dashboard operations.
 */

import { api } from '@services/enhancedApiService'

function buildLegacyAnalyticsParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== ''
      )
      .map(([key, value]) => [
        key,
        Array.isArray(value)
          ? value.join(',')
          : value
      ])
  )
}

export const legacyAnalyticsService = {
  async getMetrics(params = {}, options = {}) {
    const response = await api.get(
      '/admin/analytics/legacy/metrics',
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getDependencies(params = {}, options = {}) {
    const response = await api.get(
      '/admin/analytics/legacy/dependencies',
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getEvents(params = {}, options = {}) {
    const response = await api.get(
      '/admin/analytics/legacy/events',
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getEventById(eventId, options = {}) {
    if (!eventId) {
      throw new Error(
        'eventId is required to fetch event details'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/events/${encodeURIComponent(
        eventId
      )}`,
      options
    )
    return response.data
  },

  async getEventsByTrace(
    traceId,
    params = {},
    options = {}
  ) {
    if (!traceId) {
      throw new Error(
        'traceId is required to fetch trace events'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/events/trace/${encodeURIComponent(
        traceId
      )}`,
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getEventsByRequest(
    requestId,
    params = {},
    options = {}
  ) {
    if (!requestId) {
      throw new Error(
        'requestId is required to fetch request events'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/events/request/${encodeURIComponent(
        requestId
      )}`,
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getUserActivity(
    userId,
    params = {},
    options = {}
  ) {
    if (!userId) {
      throw new Error(
        'userId is required to fetch user activity'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/user/${encodeURIComponent(
        userId
      )}/activity`,
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getApiUsage(
    userId,
    params = {},
    options = {}
  ) {
    if (!userId) {
      throw new Error(
        'userId is required to fetch API usage'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/user/${encodeURIComponent(
        userId
      )}/api-usage`,
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getTradingAnalytics(
    userId,
    params = {},
    options = {}
  ) {
    if (!userId) {
      throw new Error(
        'userId is required to fetch trading analytics'
      )
    }
    const response = await api.get(
      `/admin/analytics/legacy/user/${encodeURIComponent(
        userId
      )}/trading`,
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  },

  async getSystemHealth(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/analytics/legacy/system/health',
      {
        ...options,
        params: buildLegacyAnalyticsParams(params)
      }
    )
    return response.data
  }
}

export const adminService = {
  async getSession() {
    const response = await api.get('/admin/session')
    return response.data
  },

  async elevateSession(totpCode) {
    const response = await api.post('/admin/session/elevate', { totpCode })
    return response.data
  },

  async revokeElevation() {
    const response = await api.delete('/admin/session/elevation')
    return response.data
  },

  async getAlertStats() {
    const response = await api.get('/mcp/alerts/stats')
    return response.data
  },

  async getOverview(params = {}, options = {}) {
    const response = await api.get('/admin/overview', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsSummary(params = {}, options = {}) {
    const response = await api.get('/admin/analytics/summary', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsTimeSeries(params = {}, options = {}) {
    const response = await api.get('/admin/analytics/timeseries', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsBreakdown(dimension, params = {}, options = {}) {
    const response = await api.get(
      `/admin/analytics/breakdowns/${encodeURIComponent(dimension)}`,
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getAnalyticsEvents(params = {}, options = {}) {
    const response = await api.get('/admin/analytics/events', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsEvent(eventId, options = {}) {
    const response = await api.get(
      `/admin/analytics/events/${encodeURIComponent(eventId)}`,
      options
    )
    return response.data
  },

  async getAnalyticsTraceEvents(traceId, options = {}) {
    const response = await api.get(
      `/admin/analytics/events/trace/${encodeURIComponent(traceId)}`,
      options
    )
    return response.data
  },

  async getAnalyticsRequestEvents(requestId, options = {}) {
    const response = await api.get(
      `/admin/analytics/events/request/${encodeURIComponent(requestId)}`,
      options
    )
    return response.data
  },

  async getAnalyticsDependencies(params = {}, options = {}) {
    const response = await api.get('/admin/analytics/dependencies', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsAuthentication(params = {}, options = {}) {
    const response = await api.get('/admin/analytics/authentication', {
      ...options,
      params
    })
    return response.data
  },

  async getAnalyticsObservability(options = {}) {
    const response = await api.get('/admin/analytics/observability', options)
    return response.data
  },

  // ── Content Administration ─────────────────────────────────────

  async getContentTaxonomy(options = {}) {
    const response = await api.get('/admin/content/taxonomy', options)
    return response.data
  },

  async uploadContentImage(
    file,
    reason,
    idempotencyKey,
    options = {}
  ) {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('reason', reason)
    const response = await api.post(
      '/admin/content/images',
      formData,
      {
        ...options,
        headers: {
          ...options.headers,
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getContentPosts(params = {}, options = {}) {
    const response = await api.get('/admin/content/posts', {
      ...options,
      params
    })
    return response.data
  },

  async getContentPost(postId, options = {}) {
    const response = await api.get(
      `/admin/content/posts/${encodeURIComponent(postId)}`,
      options
    )
    return response.data
  },

  async createContentPost(payload, idempotencyKey, options = {}) {
    const response = await api.post('/admin/content/posts', payload, {
      ...options,
      headers: {
        ...options.headers,
        'Idempotency-Key': idempotencyKey
      }
    })
    return response.data
  },

  async updateContentPost(
    postId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.patch(
      `/admin/content/posts/${encodeURIComponent(postId)}`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async transitionContentPost(
    postId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/content/posts/${encodeURIComponent(postId)}/status`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async bulkTransitionContentPosts(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/content/posts/bulk/status',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getContentComments(params = {}, options = {}) {
    const response = await api.get('/admin/content/comments', {
      ...options,
      params
    })
    return response.data
  },

  async moderateContentComment(
    commentId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/content/comments/${encodeURIComponent(
        commentId
      )}/moderation`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async bulkModerateContentComments(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/content/comments/bulk/moderation',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getContentAIQueue(params = {}, options = {}) {
    const response = await api.get(
      '/admin/content/ai-queue',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getContentAIQueuePost(postId, options = {}) {
    const response = await api.get(
      `/admin/content/ai-queue/${encodeURIComponent(
        postId
      )}`,
      options
    )
    return response.data
  },

  async reviewContentAIQueuePost(
    postId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/content/ai-queue/${encodeURIComponent(
        postId
      )}/review`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getContentSchedules(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/content/schedules',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async createContentSchedule(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/content/schedules',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async updateContentSchedule(
    scheduleId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.patch(
      `/admin/content/schedules/${encodeURIComponent(
        scheduleId
      )}`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async deleteContentSchedule(
    scheduleId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.delete(
      `/admin/content/schedules/${encodeURIComponent(
        scheduleId
      )}`,
      {
        ...options,
        data: payload,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async actionContentSchedule(
    scheduleId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/content/schedules/${encodeURIComponent(
        scheduleId
      )}/actions`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getContentAnalytics(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/content/analytics',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getContentSettings(options = {}) {
    const response = await api.get(
      '/admin/content/settings',
      options
    )
    return response.data
  },

  async updateContentSettings(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.patch(
      '/admin/content/settings',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async testContentSettingsEmail(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/content/settings/test-email',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getAIJobs(params = {}, options = {}) {
    const response = await api.get('/admin/ai/jobs', {
      ...options,
      params
    })
    return response.data
  },

  async getAIJobSummary(params = {}, options = {}) {
    const response = await api.get('/admin/ai/jobs/summary', {
      ...options,
      params
    })
    return response.data
  },

  async getAIJob(jobId, options = {}) {
    const response = await api.get(
      `/admin/ai/jobs/${encodeURIComponent(jobId)}`,
      options
    )
    return response.data
  },

  async getAIUsageSummary(params = {}, options = {}) {
    const response = await api.get('/admin/ai/usage/summary', {
      ...options,
      params
    })
    return response.data
  },

  async getAIAgentSummary(params = {}, options = {}) {
    const response = await api.get('/admin/ai/agents/summary', {
      ...options,
      params
    })
    return response.data
  },

  async getAISignalOverview(options = {}) {
    const response = await api.get('/admin/ai/signals/overview', options)
    return response.data
  },

  async getAISignalInstruments(params = {}, options = {}) {
    const response = await api.get('/admin/ai/signals/instruments', {
      ...options,
      params
    })
    return response.data
  },

  async enableAISignalScheduler(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/ai/signals/scheduler/enable',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async disableAISignalScheduler(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/ai/signals/scheduler/disable',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async queueAISignalBatch(payload, idempotencyKey, options = {}) {
    const response = await api.post(
      '/admin/ai/signals/generation/batch',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async queueAISignalInstrument(
    instrumentKey,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/ai/signals/generation/instruments/${encodeURIComponent(
        instrumentKey
      )}`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async clearAllAISignalCache(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.delete('/admin/ai/signals/cache', {
      ...options,
      data: payload,
      headers: {
        ...options.headers,
        'Idempotency-Key': idempotencyKey
      }
    })
    return response.data
  },

  async clearAISignalInstrumentCache(
    instrumentKey,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.delete(
      `/admin/ai/signals/cache/instruments/${encodeURIComponent(
        instrumentKey
      )}`,
      {
        ...options,
        data: payload,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getStats() {
    const response = await api.get('/admin/stats')
    return response.data
  },

  // ── User Management ──────────────────────────────────────────────

  /**
   * Get paginated users list
   * GET /api/admin/users
   */
  async getUsers(params = {}, options = {}) {
    const response = await api.get('/admin/users', {
      ...options,
      params
    })
    return response.data
  },

  async getCustomerSummary(options = {}) {
    const response = await api.get('/admin/users/summary', options)
    return response.data
  },

  async getAdministrators() {
    const response = await api.get('/admin/admins')
    return response.data
  },

  async updateUserRole(userId, payload, idempotencyKey, options = {}) {
    const response = await api.patch(
      `/admin/users/${userId}/role`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getRoleChangeApprovals(options = {}) {
    const response = await api.get(
      '/admin/access/role-approvals',
      options
    )
    return response.data
  },

  async approveRoleChange(
    approvalId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/access/role-approvals/${encodeURIComponent(
        approvalId
      )}/approve`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async rejectRoleChange(
    approvalId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/access/role-approvals/${encodeURIComponent(
        approvalId
      )}/reject`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async approveUser(userId, payload, idempotencyKey) {
    const response = await api.post(
      `/admin/users/${userId}/approve`,
      payload,
      {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async rejectUser(userId, { reason }, idempotencyKey) {
    const response = await api.post(
      `/admin/users/${userId}/reject`,
      { reason },
      {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async suspendUser(userId, { reason }, idempotencyKey) {
    const response = await api.post(
      `/admin/users/${userId}/suspend`,
      { reason },
      {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async reactivateUser(userId, { reason }, idempotencyKey) {
    const response = await api.post(
      `/admin/users/${userId}/reactivate`,
      { reason },
      {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async updateUserPlan(userId, payload, idempotencyKey) {
    const response = await api.patch(
      `/admin/users/${userId}/plan`,
      payload,
      {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getAuditLogs(params = {}) {
    const response = await api.get('/admin/audit-logs', { params })
    return response.data
  },

  // ── Operations Control Center ───────────────────────────────────

  async getOperationsControl(options = {}) {
    const response = await api.get('/admin/operations/control', options)
    return response.data
  },

  async getOperationsIntegrationsOverview(options = {}) {
    const response = await api.get(
      '/admin/operations/integrations/overview',
      options
    )
    return response.data
  },

  async getOperationsMcpRuntimeOverview(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/operations/mcp/overview',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getOperationsSystemHealthOverview(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/operations/health/overview',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getOperationsObservabilityOverview(
    params = {},
    options = {}
  ) {
    const response = await api.get(
      '/admin/operations/observability/overview',
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async getOperationsContractRolloverOverview(options = {}) {
    const response = await api.get(
      '/admin/operations/contract-rollover/overview',
      options
    )
    return response.data
  },

  async createOperationsContractRolloverPreview(
    payload,
    options = {}
  ) {
    const response = await api.post(
      '/admin/operations/contract-rollover/previews',
      payload,
      options
    )
    return response.data
  },

  async getOperationsContractRolloverPreview(
    previewId,
    params = {},
    options = {}
  ) {
    const response = await api.get(
      `/admin/operations/contract-rollover/previews/${encodeURIComponent(
        previewId
      )}`,
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async executeOperationsContractRolloverPreview(
    previewId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/operations/contract-rollover/previews/${encodeURIComponent(
        previewId
      )}/execute`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async activateOperationsKillSwitch(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/operations/control/kill-switch/activate',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async deactivateOperationsKillSwitch(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      '/admin/operations/control/kill-switch/deactivate',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async resumeOperationsAlgorithm(
    algorithmId,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/operations/control/kill-switch/algorithms/${encodeURIComponent(
        algorithmId
      )}/resume`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getOperationsJobsOverview(options = {}) {
    const response = await api.get(
      '/admin/operations/jobs/overview',
      options
    )
    return response.data
  },

  async getOperationsJobItems(params = {}, options = {}) {
    const response = await api.get('/admin/operations/jobs/items', {
      ...options,
      params
    })
    return response.data
  },

  async getOperationsJobItem(
    queue,
    itemId,
    params = {},
    options = {}
  ) {
    const response = await api.get(
      `/admin/operations/jobs/items/${encodeURIComponent(
        queue
      )}/${encodeURIComponent(itemId)}`,
      {
        ...options,
        params
      }
    )
    return response.data
  },

  async pauseOperationsOutbox(
    domain,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/operations/jobs/outbox/${encodeURIComponent(
        domain
      )}/pause`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async resumeOperationsOutbox(
    domain,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/operations/jobs/outbox/${encodeURIComponent(
        domain
      )}/resume`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async retryOperationsOutboxFailed(
    domain,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.post(
      `/admin/operations/jobs/outbox/${encodeURIComponent(
        domain
      )}/retry-failed`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async getAIProviderSettings(options = {}) {
    const response = await api.get('/admin/ai/providers', options)
    return response.data
  },

  async updateDefaultAIProvider(
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.put(
      '/admin/ai/providers/default',
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async updateAIProviderPlanOverride(
    plan,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.put(
      `/admin/ai/providers/plans/${encodeURIComponent(plan)}`,
      payload,
      {
        ...options,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  },

  async removeAIProviderPlanOverride(
    plan,
    payload,
    idempotencyKey,
    options = {}
  ) {
    const response = await api.delete(
      `/admin/ai/providers/plans/${encodeURIComponent(plan)}`,
      {
        ...options,
        data: payload,
        headers: {
          ...options.headers,
          'Idempotency-Key': idempotencyKey
        }
      }
    )
    return response.data
  }
}

export default adminService
